import React, { useEffect, useState } from "react";
import {
  Table,
  Row,
  Col,
  Steps,
  Spin,
  Alert,
  Popover,
  StepsProps,
  Typography,
  Flex,
} from "antd";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import {
  getReferralIncentiveDetails,
  getReferralsList,
} from "../redux/referrals/referralsSlice";
import Coin from "../assets/coins.svg";
import { IncentiveType, ReferralAction } from "../types/admin/adminEnums";
import dayjs from "dayjs";

const Tracker: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { referrals, total, loading, error } = useSelector(
    (state: RootState) => state.referrals
  );
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const getReferralDetails = async () => {
      await dispatch(getReferralsList({ page: currentPage }));
      await dispatch(getReferralIncentiveDetails());
    };

    getReferralDetails();
  }, [dispatch, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowExpand = async (record: any) => {
    setExpandedRow(expandedRow === record.Id ? null : record.Id);
  };
  const expandedRowRender = (record: any): React.ReactNode => {
    const currentMonth = dayjs().format("MMM");
    const onBoardedMonthIndex = dayjs(record.onboarded_at).month();
    const incentiveEndMonthIndex = dayjs(record.completed_at).month();

    let parsedDetails = [];
    try {
      parsedDetails = JSON.parse(record.incentive_details ?? "[]");
    } catch (error) {
      console.error("Error parsing incentive details:", error);
    }

    const steps =
      record.incentive_type === IncentiveType.PER_CASE
        ? parsedDetails.map((data: any) => ({
            title: data.amount,
            subTitle: data.cases,
            coins: data.cases,
          }))
        : Array.from(
            { length: incentiveEndMonthIndex - onBoardedMonthIndex + 1 },
            (_, i) => {
              const monthIndex = onBoardedMonthIndex + i;
              return {
                title: dayjs().month(monthIndex).format("MMM"),
                coins: dayjs().month(monthIndex).format("MMM"),
              };
            }
          );

    let currentStepIndex = 0;

    if (record.incentive_type === IncentiveType.PER_CASE) {
      const caseCount = record.incentiveData?.case_count
        ? +record.incentiveData.case_count
        : 0;

      currentStepIndex = steps.findLastIndex(
        (step: any) => caseCount >= step.coins
      );

      if (currentStepIndex === -1) {
        currentStepIndex = 0;
      }
    } else {
      currentStepIndex = steps.findIndex(
        (step: any) => step.title === currentMonth
      );
      if (currentStepIndex === -1) currentStepIndex = 0;
    }

    const customDot: StepsProps["progressDot"] = (dot, { status, index }) => {
      if (index === currentStepIndex) {
        return (
          <Popover
            content={
              <Typography.Text>
                Current Case Count :{" "}
                <span>{record?.incentiveData?.case_count ?? 0}</span>
              </Typography.Text>
            }
          >
            {dot}
          </Popover>
        );
      }
      return <span>{dot}</span>;
    };

    return (
      <ExpandedRowWrapper>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6} style={{ justifyContent: "center" }}>
            <CaseCountWrapper>
              Case Count{" "}
              {record.completed_at
                ? `within (${dayjs(record.completed_at).format("DD MMM YYYY")})`
                : ""}
            </CaseCountWrapper>
            <Flex align="center" justify="center" gap={5}>
              <strong style={{ color: "blue", fontSize: "1.5rem" }}>
                {record.incentiveData?.case_count ?? 0}
              </strong>{" "}
              Cases | Modality : {record?.modality?.replace(",", ", ")}
            </Flex>
          </Col>

          <Col
            xs={24}
            md={18}
            style={{
              overflowX: "auto",
              height: "5rem",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <StepsContainer>
              <StyledSteps
                current={currentStepIndex}
                direction="horizontal"
                size="small"
                labelPlacement="vertical"
                progressDot={customDot}
                responsive={false}
                items={steps.map((step: any) => ({
                  title: (
                    <StepItem>
                      <Flex gap={5}>
                        <img src={Coin} alt="Coin" />
                        <div>
                          {record.incentive_type === IncentiveType.PER_CASE
                            ? step.title
                            : step.title === currentMonth
                            ? "On Going"
                            : ""}
                        </div>
                      </Flex>
                      <BottomTitleContainer>
                        {record.incentive_type === IncentiveType.PER_CASE
                          ? `${step.coins} cases`
                          : `${step.coins}`}
                      </BottomTitleContainer>
                    </StepItem>
                  ),
                }))}
              />
            </StepsContainer>
          </Col>
        </Row>
      </ExpandedRowWrapper>
    );
  };

  const columns = [
    {
      title: "Organization Name",
      dataIndex: "organisation_name",
      key: "organisation_name",
    },
    { title: "Referee Name", dataIndex: "referred_name", key: "referred_name" },
    {
      title: "Mobile",
      dataIndex: "referred_mobile_number",
      key: "referred_mobile_number",
    },
    {
      title: "Status",
      key: "referral_action",
      dataIndex: "referral_action",
      render: (referral_action: string) => (
        <StatusText $status={referral_action}>{referral_action}</StatusText>
      ),
    },
    {
      title: "Reward",
      dataIndex: "reward",
      key: "reward",
      render: (_: any, record: any) => (
        <RewardContainer>
          <img src={Coin} alt="Coin" />
          <span>{record?.incentiveData?.incentive ?? 0}</span>
        </RewardContainer>
      ),
    },
    Table.EXPAND_COLUMN,
  ];

  if (loading) return <Spin size="large" />;
  if (error) return <Alert message={error} type="error" />;

  return (
    <TrackerContainer>
      <StyledTable
        columns={columns}
        dataSource={referrals}
        pagination={{
          current: currentPage,
          pageSize: 10,
          total: total,
          onChange: handlePageChange,
        }}
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedRow ? [expandedRow] : [],
          rowExpandable: (record: any) =>
            ![ReferralAction.PENDING, ReferralAction.REJECTED].includes(
              record.referral_action
            ),
          onExpand: (expanded: boolean, record: any) => handleRowExpand(record),
          expandIconColumnIndex: -1,
        }}
        rowKey="Id"
      />
    </TrackerContainer>
  );
};

export default Tracker;

// 🔹 Styled Components
const TrackerContainer = styled.div`
  width: 100%;
  overflow-x: auto; /* Enables horizontal scrolling */
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on mobile */

  @media (max-width: 768px) {
    padding-bottom: 10px;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    background-color: #f9f9f9;
    padding: 16px 8px;

    @media (max-width: 768px) {
      font-size: 14px;
      padding: 12px 6px;
    }

    @media (max-width: 480px) {
      font-size: 13px;
      padding: 8px 4px;
    }
  }

  .ant-table-tbody > tr > td {
    text-align: center;
    font-size: 14px;
    padding: 12px 8px;

    @media (max-width: 768px) {
      font-size: 13px;
      padding: 10px 6px;
    }

    @media (max-width: 480px) {
      font-size: 12px;
      padding: 8px 4px;
    }
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5;
  }

  .ant-pagination {
    margin: 16px 0;

    @media (max-width: 768px) {
      margin: 12px 0;

      .ant-pagination-item {
        min-width: 28px;
        height: 28px;
        line-height: 28px;
      }
    }

    @media (max-width: 480px) {
      margin: 8px 0;

      .ant-pagination-item {
        min-width: 24px;
        height: 24px;
        line-height: 24px;
      }
    }
  }

  @media (max-width: 480px) {
    .ant-table-row-expand-icon {
      transform: scale(0.8);
    }
  }
`;

const ExpandedRowWrapper = styled.div`
  padding: 20px;
  border-radius: 15px;
  background-color: #ebf4ff;

  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    border-radius: 10px;
  }
`;

const CaseCountWrapper = styled.div`
  strong {
    font-size: 18px;
    color: #040815;

    @media (max-width: 768px) {
      font-size: 16px;
    }

    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
`;

const StepsContainer = styled.div`
  width: 100%;
`;

const StepItem = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  img {
    width: 15px;

    @media (max-width: 768px) {
      width: 12px;
    }

    @media (max-width: 480px) {
      width: 10px;
    }
  }

  div {
    font-size: 12px;
    margin-top: 5px;

    @media (max-width: 768px) {
      font-size: 11px;
    }

    @media (max-width: 480px) {
      font-size: 10px;
    }
  }
`;

export const RewardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;

  img {
    width: 20px;
    height: 20px;

    @media (max-width: 768px) {
      width: 18px;
      height: 18px;
    }

    @media (max-width: 480px) {
      width: 16px;
      height: 16px;
    }
  }

  span {
    font-weight: bold;
    font-size: 16px;

    @media (max-width: 768px) {
      font-size: 14px;
    }

    @media (max-width: 480px) {
      font-size: 12px;
    }
  }

  button {
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 480px) {
      font-size: 12px;
    }
  }
`;

const StatusText = styled.span<{ $status: string }>`
  color: ${({ $status }) =>
    $status === "completed"
      ? "green"
      : $status === "onboarded"
      ? "blue"
      : $status === "referred"
      ? "orange"
      : "gray"};

  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const StyledSteps = styled(Steps)`
  .ant-steps-item {
    height: 1rem !important;
  }
  .ant-steps-item-title {
    position: relative;
    bottom: 55px;
  }
`;

const BottomTitleContainer = styled.div`
  position: absolute;
  top: 2.5rem;
  width: 5.5rem;
`;
